import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { DisplaySettings } from '@glyph/protocol-ts';
import { useApi, useCaptions, useClient, useDevice, useDeviceId, useProfiles, useSaveSettings, useStatus } from '@glyph/core';
import { CloudPresetsCard } from './PresetsScreen';
import {
  Button,
  Card,
  CardHeader,
  ColorField,
  ColorSwatch,
  Field,
  ListRow,
  PageHeader,
  Pill,
  PositionGrid,
  Section,
  SegmentedControl,
  Select,
  Slider,
  Switch,
  SwitchRow,
  VideoFramePreview,
  positionToAlign,
  useToast,
  type Position,
  cn,
} from '@glyph/ui';
import {
  FONT_FAMILIES,
  KEY_PRESETS,
  bgLabel,
  captionLines,
  displayToMock,
  displayToPosition,
  isBlank,
  msFromSpeed,
  pctToPx,
  pxToPct,
  speedFromMs,
  upper,
} from './shared';

export interface CaptionsScreenProps {
  deviceId?: string;
}

const OPEN_KEY = 'glyph-captions-groups';
const DEMO_LINES = ['Good morning everyone and welcome back', 'to day two of the summit.'];

/** Captions: presets, position, type, box, behaviour. Every change is saved to the device as you make it
 * (debounced); "Save as preset" keeps the current look under a name. */
export function CaptionsScreen({ deviceId }: CaptionsScreenProps) {
  const dev = useDevice(deviceId);
  const s = useSaveSettings(deviceId);
  const profiles = useProfiles(deviceId);
  const status = useStatus(deviceId);
  const caps = useCaptions(deviceId);
  const toast = useToast();
  const [showSafe, setShowSafe] = useState(false);
  const [profile, setProfile] = useState<string>('Custom');
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try {
      return {
        position: true,
        type: true,
        box: true,
        behaviour: true,
        ...JSON.parse(localStorage.getItem(OPEN_KEY) ?? '{}'),
      };
    } catch {
      return { position: true, type: true, box: true, behaviour: true };
    }
  });
  const toggleGroup = (k: string) =>
    setOpen((o) => {
      const n = { ...o, [k]: !o[k] };
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify(n));
      } catch {
        /* private mode */
      }
      return n;
    });
  const d = s.draft?.display;
  const dirty = s.isDirty('display');
  const api = useApi();
  const client = useClient();
  const previewId = useDeviceId(deviceId);

  // Live preview: every edit goes to the renderer straight away (throttled to ~60 ms locally, 200 ms
  // through the relay) as a temporary style, so dragging a position or a slider is seen on the output
  // as it happens; the 500 ms auto-save below makes it permanent with the same values.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewPending = useRef<DisplaySettings | null>(null);
  const previewMs = client.kind === 'local' ? 60 : 200;
  const pushPreview = (style: DisplaySettings) => {
    const send = (st: DisplaySettings) => {
      previewPending.current = null;
      void api.previewDisplay(previewId, st).catch(() => undefined);
      previewTimer.current = setTimeout(() => {
        previewTimer.current = null;
        if (previewPending.current) send(previewPending.current);
      }, previewMs);
    };
    if (previewTimer.current) previewPending.current = style;
    else send(style);
  };

  // Auto-save: the device is the source of truth, so a change lands there 500 ms after the last edit.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!dirty) return;
    const t = setTimeout(
      () =>
        void s
          .save(['display'])
          .catch((e: unknown) => toast.push(e instanceof Error ? e.message : String(e), 'warn')),
      500,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.pendingPatch.display, dirty]);

  if (!d) return <Section />;
  const up = (p: Partial<DisplaySettings>) => {
    s.update('display', p);
    pushPreview({ ...d, ...p });
    setProfile('Custom');
  };
  const pos = displayToPosition(d);
  // The grid places the block; how lines align inside it is the Type card's "Align" control.
  const setPos = (p: Position) => {
    const a = positionToAlign(p);
    up({ v_align: a.v, h_align: a.h });
  };
  const presetNames = profiles.data ?? [];
  const applyPreset = async (name: string) => {
    await profiles.load(name);
    setProfile(name);
    toast.push(`Preset ${name} loaded`, 'ok');
  };
  const savePreset = async () => {
    const name = window.prompt('Preset name', profile === 'Custom' ? '' : profile);
    if (!name) return;
    if (dirty) await s.save(['display']);
    await profiles.save(name);
    setProfile(name);
    toast.push(`Saved preset ${name}`, 'ok');
  };

  const running = status.data?.engine?.running ?? false;
  const blank = isBlank(status.data);
  const lines =
    running && !blank ? captionLines(caps.finals, caps.partial, d) : DEMO_LINES.slice(0, d.max_lines);
  const frame = (hideCorners?: boolean) => (
    <VideoFramePreview
      background={blank ? 'boot' : 'chroma'}
      bootColor={d.key_color}
      mock={displayToMock(d, lines)}
      showSafeArea={showSafe}
      topLeft="PREVIEW · 1920×1080"
      topRight={bgLabel(d.key_color, blank)}
      cornerMarks={!hideCorners}
    />
  );
  const box = d.box.enabled ? (d.box.per_line ? 'line' : 'band') : 'none';
  const setBox = (v: string) => up({ box: { ...d.box, enabled: v !== 'none', per_line: v === 'line' } });
  const marginPx = pctToPx(d.safe_area_pct);
  const saveMeta = s.isSaving ? 'SAVING' : dirty ? 'UNSAVED' : 'SAVED ON DEVICE';
  const weights = [
    { value: 400, label: 'Regular' },
    { value: 500, label: 'Medium' },
    { value: 600, label: 'Semibold' },
    { value: 700, label: 'Bold' },
  ];
  const modes = [
    { value: 'rollup' as const, label: 'Roll-up' },
    { value: 'popon' as const, label: 'Pop-on' },
    { value: 'painton' as const, label: 'Paint-on' },
  ];
  const aligns = [
    { value: 'left' as const, label: 'Left' },
    { value: 'center' as const, label: 'Centre' },
    { value: 'right' as const, label: 'Right' },
  ];
  const boxes = [
    { value: 'none', label: 'None' },
    { value: 'band', label: 'Band' },
    { value: 'line', label: 'Per line' },
  ];
  const lineOpts = [1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }));
  const familySelect = (block: boolean) => (
    <Select
      block={block}
      value={d.font_family}
      onChange={(v) => up({ font_family: v })}
      options={[...FONT_FAMILIES.filter((f) => f !== d.font_family), d.font_family]
        .sort()
        .map((f) => ({ value: f, label: f }))}
      aria-label="Family"
    />
  );

  /* One control set rendered twice: `m` (mobile) uses row primitives, desktop uses padded cards. */
  const build = (m: boolean) => {
    const slider = (
      label: string,
      value: number,
      min: number,
      max: number,
      step: number,
      onChange: (v: number) => void,
      unit?: string,
      format?: (v: number) => string,
      disabled?: boolean,
    ) => (
      <Slider
        row={m}
        label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        unit={unit}
        format={format}
        onChange={onChange}
        disabled={disabled}
      />
    );
    const seg = <V extends string | number>(
      label: string,
      value: V,
      onChange: (v: V) => void,
      options: readonly { value: V; label: string }[],
    ) =>
      m ? (
        <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
          <span className="text-lg text-fg">{label}</span>
          <SegmentedControl fullWidth value={value} onChange={onChange} options={options} />
        </div>
      ) : (
        <Field label={label}>
          <SegmentedControl value={value} onChange={onChange} options={options} />
        </Field>
      );
    const toggle = (label: string, checked: boolean, onChange: (v: boolean) => void) =>
      m ? (
        <SwitchRow title={label} checked={checked} onChange={onChange} />
      ) : (
        <div className="flex items-center justify-between">
          <span className="micro text-fg4">{label}</span>
          <Switch checked={checked} onChange={onChange} label={label} />
        </div>
      );
    const colour = (label: string, value: string, onChange: (hex: string) => void) =>
      m ? (
        <ListRow title={label} trailing={<ColorField value={value} onChange={onChange} label={label} />} />
      ) : (
        <Field label={label}>
          <ColorField value={value} onChange={onChange} label={label} />
        </Field>
      );
    // One panel, one group per category; a closed group slides the others up.
    // One panel, one group per category. Thin white header with a 2 px outline; an open group's
    // whole block carries the same outline so each category reads as its own box.
    // One group per category: a white rounded card like the rest of the page, separated by the page
    // background; the header is the only control (bold title, summary, chevron).
    const group = (key: string, t: string, meta: string | undefined, children: ReactNode) => {
      const isOpen = open[key] !== false;
      return (
        <section key={key} className="rounded-2xl border border-line bg-panel md:rounded-xl">
          <button
            type="button"
            aria-expanded={isOpen}
            onClick={() => toggleGroup(key)}
            className={cn(
              'flex w-full items-center justify-between gap-3 border-0 bg-transparent px-4 text-left',
              isOpen ? 'pb-2 pt-3.5' : 'py-3.5',
            )}
          >
            <span className="text-lg font-semibold leading-none">{t}</span>
            <span className="flex items-center gap-2.5">
              {meta && <span className="meta text-fg4">{meta}</span>}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className={cn('transition-transform', isOpen ? 'rotate-180 text-accent' : 'text-fg3')}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </button>
          {isOpen && <div className={m ? 'pb-1' : 'flex flex-col gap-3.5 px-4 pb-4 pt-1'}>{children}</div>}
        </section>
      );
    };
    const o = d.outline;
    const ts = d.text_shadow;
    const bs = d.box_shadow;
    return (
      <>
        {group(
          'position',
          'Position',
          pos,
          <>
            {m ? (
              <div className="flex items-center justify-between gap-4 px-4 pb-3.5 pt-1.5">
                <PositionGrid value={pos} onChange={setPos} />
                <div className="flex flex-col items-end gap-2">
                  <span className="text-lg text-fg">Show safe area</span>
                  <Switch checked={showSafe} onChange={setShowSafe} label="Show safe area" />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <PositionGrid value={pos} onChange={setPos} />
                <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                  {slider('Safe margin', marginPx, 0, 200, 4, (v) => up({ safe_area_pct: pxToPct(v) }), 'px')}
                  {slider('Width', d.width_pct, 40, 100, 2, (v) => up({ width_pct: v }), '%')}
                </div>
              </div>
            )}
            {m && slider('Safe margin', marginPx, 0, 200, 4, (v) => up({ safe_area_pct: pxToPct(v) }), 'px')}
            {m && slider('Width', d.width_pct, 40, 100, 2, (v) => up({ width_pct: v }), '%')}
            {slider(
              'Offset X',
              pctToPx(d.offset_x_pct),
              -400,
              400,
              4,
              (v) => up({ offset_x_pct: pxToPct(v) }),
              'px',
            )}
            {slider(
              'Offset Y',
              pctToPx(d.offset_y_pct),
              -400,
              400,
              4,
              (v) => up({ offset_y_pct: pxToPct(v) }),
              'px',
            )}
            {!m && toggle('Show safe area', showSafe, setShowSafe)}
          </>,
        )}
        {group(
          'type',
          'Type',
          `${d.font_family} ${d.font_size_px}px`,
          <>
            {m ? (
              <ListRow title="Family" trailing={familySelect(false)} />
            ) : (
              <Field label="Family">{familySelect(true)}</Field>
            )}
            {slider('Size', d.font_size_px, 24, 160, 2, (v) => up({ font_size_px: v }), 'px')}
            {seg('Weight', d.font_weight, (v) => up({ font_weight: v }), weights)}
            {seg('Align', d.text_align, (v) => up({ text_align: v }), aligns)}
            {slider('Tracking', d.letter_spacing_px, -4, 12, 1, (v) => up({ letter_spacing_px: v }), 'px')}
            {slider(
              'Line height',
              d.line_height,
              0.9,
              2,
              0.05,
              (v) => up({ line_height: Math.round(v * 100) / 100 }),
              undefined,
              (x) => x.toFixed(2),
            )}
            {toggle('Uppercase', d.text_transform === 'upper', (v) =>
              up({ text_transform: v ? 'upper' : 'none' }),
            )}
            {toggle('Italic', d.font_style === 'italic', (v) => up({ font_style: v ? 'italic' : 'normal' }))}
            {colour('Text colour', d.text_color, (hex) => up({ text_color: hex }))}
            {toggle('Outline', o.enabled, (v) => up({ outline: { ...o, enabled: v } }))}
            {o.enabled && colour('Outline colour', o.color, (hex) => up({ outline: { ...o, color: hex } }))}
            {o.enabled &&
              slider('Outline width', o.width, 0, 20, 1, (v) => up({ outline: { ...o, width: v } }), 'px')}
            {toggle('Text shadow', ts.enabled, (v) => up({ text_shadow: { ...ts, enabled: v } }))}
            {ts.enabled &&
              colour('Shadow colour', ts.color, (hex) => up({ text_shadow: { ...ts, color: hex } }))}
            {ts.enabled &&
              slider('Shadow blur', ts.blur, 0, 80, 1, (v) => up({ text_shadow: { ...ts, blur: v } }), 'px')}
            {ts.enabled &&
              slider(
                'Shadow offset X',
                ts.offset_x,
                -40,
                40,
                1,
                (v) => up({ text_shadow: { ...ts, offset_x: v } }),
                'px',
              )}
            {ts.enabled &&
              slider(
                'Shadow offset Y',
                ts.offset_y,
                -40,
                40,
                1,
                (v) => up({ text_shadow: { ...ts, offset_y: v } }),
                'px',
              )}
            {ts.enabled &&
              slider(
                'Shadow opacity',
                ts.opacity,
                0,
                100,
                5,
                (v) => up({ text_shadow: { ...ts, opacity: v } }),
                '%',
              )}
          </>,
        )}
        {group(
          'box',
          'Box',
          box === 'none' ? 'off' : box,
          <>
            {seg('Caption box', box, setBox, boxes)}
            {d.box.enabled &&
              colour('Box colour', d.box.color, (hex) => up({ box: { ...d.box, color: hex } }))}
            {d.box.enabled &&
              slider(
                'Box opacity',
                d.box.opacity,
                0,
                100,
                5,
                (v) => up({ box: { ...d.box, opacity: v } }),
                '%',
              )}
            {d.box.enabled &&
              slider(
                'Padding X',
                d.box.padding_x,
                0,
                120,
                2,
                (v) => up({ box: { ...d.box, padding_x: v } }),
                'px',
              )}
            {d.box.enabled &&
              slider(
                'Padding Y',
                d.box.padding_y,
                0,
                120,
                2,
                (v) => up({ box: { ...d.box, padding_y: v } }),
                'px',
              )}
            {d.box.enabled &&
              slider(
                'Corner radius',
                d.box.radius,
                0,
                80,
                1,
                (v) => up({ box: { ...d.box, radius: v } }),
                'px',
              )}
            {d.box.enabled &&
              toggle('Box shadow', bs.enabled, (v) => up({ box_shadow: { ...bs, enabled: v } }))}
            {d.box.enabled &&
              bs.enabled &&
              colour('Box shadow colour', bs.color, (hex) => up({ box_shadow: { ...bs, color: hex } }))}
            {d.box.enabled &&
              bs.enabled &&
              slider(
                'Box shadow blur',
                bs.blur,
                0,
                120,
                2,
                (v) => up({ box_shadow: { ...bs, blur: v } }),
                'px',
              )}
            {d.box.enabled &&
              bs.enabled &&
              slider(
                'Box shadow offset X',
                bs.offset_x,
                -60,
                60,
                1,
                (v) => up({ box_shadow: { ...bs, offset_x: v } }),
                'px',
              )}
            {d.box.enabled &&
              bs.enabled &&
              slider(
                'Box shadow offset Y',
                bs.offset_y,
                -60,
                60,
                1,
                (v) => up({ box_shadow: { ...bs, offset_y: v } }),
                'px',
              )}
            {d.box.enabled &&
              bs.enabled &&
              slider(
                'Box shadow opacity',
                bs.opacity,
                0,
                100,
                5,
                (v) => up({ box_shadow: { ...bs, opacity: v } }),
                '%',
              )}
            {colour('Key colour', d.key_color, (hex) => up({ key_color: hex }))}
            <div className={m ? 'flex flex-wrap gap-2 px-4 pb-3.5' : 'flex flex-wrap gap-2'}>
              {KEY_PRESETS.map((k) => (
                <ColorSwatch key={k.hex} color={k.hex} label={k.label} selected={d.key_color.toLowerCase() === k.hex.toLowerCase()} onClick={() => up({ key_color: k.hex })} />
              ))}
            </div>
          </>,
        )}
        {group(
          'behaviour',
          'Behaviour',
          `${d.mode} · ${d.max_lines} lines`,
          <>
            {seg('Mode', d.mode, (v) => up({ mode: v }), modes)}
            {seg('Lines', d.max_lines, (v) => up({ max_lines: v }), lineOpts)}
            {slider(
              'Scroll speed',
              speedFromMs(d.scroll_duration_ms),
              0.5,
              3,
              0.1,
              (v) => up({ scroll_duration_ms: msFromSpeed(v) }),
              undefined,
              (x) => `${x.toFixed(1)} ×`,
            )}
            {slider(
              'Hold after silence',
              Math.round(d.auto_clear_ms / 1000),
              2,
              20,
              1,
              (v) => up({ auto_clear_ms: v * 1000 }),
              's',
            )}
          </>,
        )}
      </>
    );
  };

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Caption display"
        meta={`${dev.data?.name ?? ''} · preset ${upper(profile)} · ${saveMeta}`}
        actions={<Button onClick={() => void savePreset()}>Save as preset</Button>}
      />

      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        <div className="sticky top-[52px] z-[5] -mx-4 -mt-4 flex flex-col gap-2.5 border-b border-line bg-bg px-4 py-3">
          {frame(true)}
          <div className="scrollbar-none flex gap-2 overflow-x-auto pb-0.5">
            {presetNames.map((n) => (
              <Pill key={n} selected={profile === n} onClick={() => void applyPreset(n)}>
                {n}
              </Pill>
            ))}
            <Pill onClick={() => void savePreset()}>+ Save preset</Pill>
          </div>
        </div>
        <div className="flex flex-col gap-3">{build(true)}</div>
      </div>

      {/* desktop */}
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <div className="sticky top-[72px] flex min-w-0 flex-col gap-3.5" style={{ flex: '1 1 520px' }}>
          {frame()}
          <Card>
            <CardHeader dense title="Presets" meta="ON THIS DEVICE" />
            {presetNames.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => void applyPreset(n)}
                className="flex w-full items-center justify-between border-0 border-t border-line bg-transparent px-4 py-[11px] text-left text-md font-normal text-fg hover:bg-hover"
              >
                <span>{n}</span>
                <span className="meta text-accent">{profile === n ? 'ACTIVE' : ''}</span>
              </button>
            ))}
          </Card>
          {deviceId && <CloudPresetsCard deviceId={deviceId} deviceName={dev.data?.name ?? 'this unit'} />}
        </div>
        <div
          className="scrollbar-thin flex min-w-[320px] flex-col gap-3 overflow-y-auto pr-1"
          style={{ flex: '1 1 380px', maxHeight: 'calc(100vh - 128px)' }}
        >
          {build(false)}
        </div>
      </div>
    </Section>
  );
}
